'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Eye, EyeOff, LoaderCircleIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Alert, AlertIcon, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getSigninSchema } from '../forms/signin-schema';
import { BackendAuth } from '@/lib/auth-backend';
import { fetchAuthBootstrap } from '@/lib/auth-bootstrap';
import Head from 'next/head';
import { useAuthSettings } from '@/hooks/useAuthSettings';
import { usePasskey } from '@/hooks/usePasskey';
import { useDebounce } from '@/hooks/useDebounce';
import PasskeyLoginButton from '@/components/auth/PasskeyLoginButton';
import PasskeySetupModal from '@/components/auth/PasskeySetupModal';
import MFAVerificationModal from '@/components/auth/MFAVerificationModal';
import OAuthButtons from '@/components/auth/OAuthButtons';
import IdAustriaButton from '@/components/auth/IdAustriaButton';
import IdAustriaLinkDialog from '@/components/auth/IdAustriaLinkDialog';
import { ScreenLoader } from '@/components/common/screen-loader';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from 'sonner';

/**
 * Validates redirect URLs to prevent open redirect attacks
 * Only allows relative paths or same-origin URLs
 */
const isValidRedirectUrl = (url) => {
  if (!url) return true; // null/undefined defaults to dashboard

  try {
    // Block dangerous protocols
    if (url.startsWith('javascript:') || url.startsWith('data:')) {
      console.warn('[Security] Blocked dangerous redirect protocol:', url);
      return false;
    }

    // Allow relative URLs (most common case)
    if (url.startsWith('/')) {
      return true;
    }

    // For absolute URLs, verify same origin
    const urlObj = new URL(url, window.location.origin);
    const isSameOrigin = urlObj.origin === window.location.origin;

    if (!isSameOrigin) {
      console.warn('[Security] Blocked external redirect:', url);
    }

    return isSameOrigin;
  } catch (error) {
    console.error('[Security] Invalid redirect URL:', url, error);
    return false;
  }
};

export default function SigninClient({ initialOauthCompleting = false }) {
  const router = useRouter();
  const { t } = useTranslation();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  // eID/OAuth-Callback: true solange /signin?oauth_success=…&token=… verarbeitet
  // wird — dann rendert die Seite einen Loader statt des Formulars (kommt per
  // Prop aus page.jsx, damit schon das Server-HTML den Loader zeigt).
  const [oauthCompleting, setOauthCompleting] = useState(initialOauthCompleting);
  const [domainInfo, setDomainInfo] = useState(null);
  const [isLoadingDomain, setIsLoadingDomain] = useState(true);
  const [returnUrl, setReturnUrl] = useState(null);

  // Auth Settings & MFA
  const { settings, loading: settingsLoading, isPasskeyEnabled, isMFAEnabled, isOAuthEnabled, isPasswordEnabled, enabledOAuthProviders, isIdAustriaAvailable } = useAuthSettings();
  const [showMFAModal, setShowMFAModal] = useState(false);
  // Mehrere Konten mit derselben E-Mail-Adresse (verschiedene Organisationen):
  // { token, organisationen: [{key, organisation_name, username}], loginValue }
  const [accountSelection, setAccountSelection] = useState(null);
  const [selectingKey, setSelectingKey] = useState(null);
  const [tempToken, setTempToken] = useState(null);

  // ID Austria JIT-linking: set when /signin?link_idaustria=<token> is hit.
  const [idAustriaLinkToken, setIdAustriaLinkToken] = useState(null);

  // Passkey Availability (Amazon/Google-Style)
  const { checkAvailability, loginWithPasskey, loginWithConditionalUI, isConditionalMediationAvailable, isLoading: passkeyLoading } = usePasskey();
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyDevices, setPasskeyDevices] = useState([]);
  const [checkingPasskey, setCheckingPasskey] = useState(false);
  const [showPasskeySetupModal, setShowPasskeySetupModal] = useState(false);
  const [showAlternativeLogin, setShowAlternativeLogin] = useState(false);

  // Conditional UI (WebAuthn autofill): runs a passive credentials.get in the background.
  // The controller lets us abort it the moment the user commits to another flow (password
  // submit / explicit passkey button) so two credentials.get() calls never compete.
  const conditionalAbortRef = useRef(null);

  // Shared redirect after any successful passkey login (conditional or explicit).
  const redirectAfterLogin = () => {
    const redirectTarget = returnUrl || '/dashboard';
    setTimeout(() => {
      window.location.href = redirectTarget;
    }, 100);
  };

  // Abort the passive conditional-UI credentials.get (if running) before starting any
  // competing WebAuthn / password flow.
  const abortConditionalUI = () => {
    if (conditionalAbortRef.current) {
      conditionalAbortRef.current.abort();
      conditionalAbortRef.current = null;
    }
  };

  // Signup availability based on domain config
  const [signupEnabled, setSignupEnabled] = useState(false);

  // Cache system for domain data
  const getCacheKey = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    return `xoffi_domain_cache_${hostname}`;
  };

  const getCachedDomainData = () => {
    try {
      const cacheKey = getCacheKey();
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        const data = JSON.parse(cached);

        // Cache is valid for 24 hours
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          return data.domainInfo;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (err) {
      // Cache read failed silently
    }
    return null;
  };

  const setCachedDomainData = (domainInfo) => {
    try {
      const cacheKey = getCacheKey();
      const cacheData = {
        domainInfo,
        timestamp: Date.now(),
        domain: window.location.hostname
      };
      const cacheJson = JSON.stringify(cacheData);
      localStorage.setItem(cacheKey, cacheJson);
      // Cache saved successfully
    } catch (err) {
      // Cache write failed silently
    }
  };

  // Cache management
  const clearDomainCache = () => {
    try {
      localStorage.removeItem(getCacheKey());
    } catch (err) {
      // Cache clear failed silently
    }
  };

  // Debug functions (only in development)
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      window.xoffiDebug = {
        clearCache: clearDomainCache,
        showCache: () => getCachedDomainData(),
        cacheKey: getCacheKey(),
        localStorage: () => {
          const keys = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('xoffi_domain_cache_')) {
              keys.push(key);
            }
          }
          return keys;
        },
        forceCache: (data) => setCachedDomainData(data || { domain: { name_login: 'Test' } })
      };
    }
  }, []);

  const form = useForm({
    resolver: zodResolver(getSigninSchema()),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Watch email field changes with debounce
  const emailValue = form.watch('email') || '';
  const debouncedEmail = useDebounce(emailValue, 500);

  // Extract returnUrl from URL on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const returnParam = urlParams.get('return');
      // console.log('[Signin] Return URL from window.location:', returnParam);
      // Validate redirect URL to prevent open redirect attacks
      setReturnUrl(isValidRedirectUrl(returnParam) ? returnParam : null);
    }
  }, []);

  // Bereits eingeloggt auf /signin gelandet (z.B. Middleware-Root-Redirect bei
  // localStorage-Token ohne auth-token-Cookie): Token gegen Backend verifizieren
  // und direkt weiterleiten statt Login-Form zu zeigen. Bei 401 raeumt apiCall
  // die stale Session selbst auf (auto-logout) — Form bleibt stehen.
  useEffect(() => {
    if (oauthCompleting) return; // OAuth-Callback läuft — kein Health-Check-Race
    if (!BackendAuth.isAuthenticated()) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await BackendAuth.apiCall('/api/auth/health');
        if (!cancelled && response?.ok) {
          const urlParams = new URLSearchParams(window.location.search);
          const returnParam = urlParams.get('return');
          const target = isValidRedirectUrl(returnParam) && returnParam ? returnParam : '/dashboard';
          window.location.href = target;
        }
      } catch { /* Backend offline/Netzfehler — Login-Form stehen lassen */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Domain-Daten laden (mit Cache für instant loading)
  useEffect(() => {
    // Set background transparent immediately
    document.body.classList.add('auth-bg-custom');

    // Try to load from cache first for instant loading
    const cachedData = getCachedDomainData();

    if (cachedData) {
      setDomainInfo(cachedData);
      setIsLoadingDomain(false);

      // Set dynamic page title from cache
      if (cachedData.domain?.title || cachedData.domain?.name) {
        document.title = `Anmelden | ${cachedData.domain.title || cachedData.domain.name}`;
      }

      // Set background image from cache immediately
      if (cachedData.domain?.background_login_url) {
        setBackgroundImageFromCache(cachedData.domain.background_login_url);
      }
    }

    const loadDomainData = async () => {
      try {
        if (!cachedData) {
          setIsLoadingDomain(true);
        }

        // Try DomainCheck first (real MySQL data) — bevorzugt aus dem
        // gebündelten auth-bootstrap-Call (geteilt mit useAuthSettings),
        // Fallback auf den Einzel-Endpoint.
        let domainData = null;
        try {
          const bootstrap = await fetchAuthBootstrap();
          domainData = bootstrap?.domaincheck || null;
        } catch {
          // Fallback unten auf /api/domaincheck
        }
        if (!domainData) {
          // credentials: 'omit' prevents sending cookies with old/invalid tokens
          const domainResponse = await fetch('/api/domaincheck', { credentials: 'omit' });
          if (domainResponse.ok) {
            domainData = await domainResponse.json();
          }
        }
        if (domainData) {
          if (domainData.success && domainData.found_in_database) {

            // Domain-Daten in Theme-Format konvertieren für Kompatibilität
            const themeCompatibleData = {
              domain: {
                id: domainData.domain_data.id,
                domain: domainData.domain_data.domain,
                name: domainData.domain_data.name,
                name_login: domainData.domain_data.name_login,
                title: domainData.domain_data.title,  // Keep title separate from name_login
                description: domainData.domain_data.description || `Willkommen bei ${domainData.domain_data.name_login}`,
                logo: domainData.domain_data.logo_light || domainData.domain_data.logo || '/demo1/media/logos/default.svg',
                logo_light: domainData.domain_data.logo_light,
                logo_dark: domainData.domain_data.logo_dark,
                organization: domainData.domain_data.organisation,
                theme: domainData.domain_data.theme,
                background_login: domainData.domain_data.background_login,
                favicon: domainData.domain_data.favicon,
                primary_color: domainData.domain_data.organisation === 115 ? '#1E40AF' : '#009ef7',
                mysql_source: true // Markierung dass Daten aus MySQL kommen
              }
            };

            // Update only if data changed
            const hasChanges = !cachedData ||
              JSON.stringify(cachedData.domain) !== JSON.stringify(themeCompatibleData.domain);

            if (hasChanges) {
              setDomainInfo(themeCompatibleData);
            }

            // Set dynamic page title from domain config (prefer title, fallback to name)
            const domainDisplayName = themeCompatibleData.domain?.title || themeCompatibleData.domain?.name || 'XOFFI';
            document.title = `Anmelden | ${domainDisplayName}`;

            // Cache new data with background image (for next time)
            if (domainData.domain_data.background_login_url) {
              themeCompatibleData.domain.background_login_url = domainData.domain_data.background_login_url;
            }
            setCachedDomainData(themeCompatibleData);

            // Set login background directly (optimized URL from backend)
            if (domainData.domain_data.background_login_url) {
              const backgroundUrl = domainData.domain_data.background_login_url;

              // Preload image for smoother transition with fade-in
              const img = new Image();
              img.onload = () => {
                // Inject <style> tag so URL resolves against document (not CDN CSS file)
                const absoluteUrl = backgroundUrl.startsWith('/') ? `${window.location.origin}${backgroundUrl}` : backgroundUrl;
                let styleEl = document.getElementById('xoffi-auth-bg-style');
                if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = 'xoffi-auth-bg-style'; document.head.appendChild(styleEl); }
                styleEl.textContent = `body.auth-bg-custom::before { background-image: url('${absoluteUrl}') !important; }`;
                document.documentElement.style.setProperty('--auth-bg-image', `url('${absoluteUrl}')`);

                // Smooth fade-in after short delay
                setTimeout(() => {
                  document.body.classList.add('bg-loaded');
                }, 150);
              };

              img.onerror = () => {
                // Background image failed to load silently
              };

              img.src = backgroundUrl;
            }
            return;
          }
        }

        // Fallback: Use Theme API
        // credentials: 'omit' prevents sending cookies with old/invalid tokens
        const themeResponse = await fetch('/api/theme', { credentials: 'omit' });
        if (themeResponse.ok) {
          const themeData = await themeResponse.json();

          // Update only if data changed (Fallback)
          const hasChanges = !cachedData ||
            JSON.stringify(cachedData.domain) !== JSON.stringify(themeData.data.domain);

          if (hasChanges) {
            setDomainInfo(themeData.data);
          }

          // Cache fallback data with background image (for next time)
          if (themeData.data?.domain?.background_login) {
            // For fallback: add external asset URL
            const cleanPath = themeData.data.domain.background_login.startsWith('/') ?
              themeData.data.domain.background_login.substring(1) : themeData.data.domain.background_login;
            if (cleanPath.startsWith('vendor/') || cleanPath.startsWith('backend/') || cleanPath.startsWith('de/')) {
              themeData.data.domain.background_login_url = `/api/external-assets/${cleanPath}`;
            } else {
              themeData.data.domain.background_login_url = themeData.data.domain.background_login;
            }
          }
          setCachedDomainData(themeData.data);

          // Fallback: Set background via old method (Theme API doesn't have optimized URLs)
          if (themeData.data?.domain?.background_login) {
            setLoginBackgroundFallback(themeData.data.domain.background_login);
          }
        }
      } catch (err) {
        // Domain/Theme loading failed silently
      } finally {
        // Only end loading if we had no cached data
        if (!cachedData) {
          setIsLoadingDomain(false);
        }
      }
    };

    loadDomainData();

    // CLEANUP: Entferne alle Auth-Background-Styles beim Verlassen der Komponente
    return () => {
      // Entferne CSS-Klassen
      document.body.classList.remove('auth-bg-custom');
      document.body.classList.remove('bg-loaded');

      // Entferne Hintergrundbild CSS-Variable
      document.documentElement.style.removeProperty('--auth-bg-image');

      // Entferne alle gesetzten Background-Styles
      document.body.style.removeProperty('background-size');
      document.body.style.removeProperty('background-position');
      document.body.style.removeProperty('background-image');
      document.body.style.removeProperty('background-attachment');
      document.body.style.removeProperty('background-repeat');

      // Debug: Cleanup erfolgreich (nur in Development)
      if (process.env.NODE_ENV === 'development') {
        // console.log('🧹 Login background cleanup completed');
      }
    };
  }, []);

  // Load signup config to check if registration is enabled for this domain
  useEffect(() => {
    const loadSignupConfig = async () => {
      try {
        // Bevorzugt aus dem gebündelten auth-bootstrap-Call, Fallback Einzel-Endpoint
        let data = null;
        try {
          const bootstrap = await fetchAuthBootstrap();
          data = bootstrap?.signup_config || null;
        } catch {
          // Fallback unten
        }
        if (!data) {
          // credentials: 'omit' prevents sending cookies with old/invalid tokens
          const response = await fetch('/api/auth/signup/domain-config', { credentials: 'omit' });
          if (response.ok) {
            data = await response.json();
          }
        }
        if (data?.success && data.data) {
          // Check if registration is enabled (new_org or existing_org_only)
          setSignupEnabled(data.data.registration_enabled === true);
        }
      } catch (err) {
        // Signup config loading failed silently - default to not showing signup link
        console.error('Failed to load signup config:', err);
      }
    };

    loadSignupConfig();
  }, []);

  // OAuth Callback Handler - check for OAuth success in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthSuccess = params.get('oauth_success');
    const token = params.get('token');
    const isNew = params.get('is_new');
    const oauthError = params.get('error');
    const errorMessage = params.get('message');

    // ── ID Austria: JIT account-linking prompt ───────────────────────────────
    // Backend bounced here because the authenticated wbPK is not yet linked to
    // an account. Open the link dialog (email+password → complete-link).
    const linkIdAustria = params.get('link_idaustria');
    if (linkIdAustria) {
      setIdAustriaLinkToken(linkIdAustria);
      // Strip the token from the URL (don't leave it in history/back-button).
      window.history.replaceState({}, '', '/signin');
      return;
    }

    // ── ID Austria: status / error toasts (sonner) ───────────────────────────
    const idAustriaStatus = params.get('id_austria');
    if (idAustriaStatus === 'linked') {
      toast.success(
        t('auth.id_austria.linked_success', {
          defaultValue: 'ID Austria erfolgreich verknüpft.',
          editable: false,
        }),
      );
      window.history.replaceState({}, '', '/signin');
      return;
    }
    if (oauthError === 'id_austria_conflict') {
      toast.error(
        t('auth.id_austria.error_conflict', {
          defaultValue:
            'Diese ID Austria ist bereits mit einem anderen Konto verknüpft.',
          editable: false,
        }),
      );
      window.history.replaceState({}, '', '/signin');
      return;
    }
    if (oauthError === 'id_austria_failed') {
      toast.error(
        t('auth.id_austria.error_failed', {
          defaultValue: 'Anmeldung mit ID Austria fehlgeschlagen.',
          editable: false,
        }),
      );
      window.history.replaceState({}, '', '/signin');
      return;
    }

    if (oauthError) {
      // OAuth failed - show error
      console.error('[OAuth] Login failed:', errorMessage || oauthError);
      setError(errorMessage || 'OAuth authentication failed');
      // Clean URL
      window.history.replaceState({}, '', '/signin');
      return;
    }

    if (oauthSuccess === 'true' && token) {
      // console.log('[OAuth] Login successful, storing token');

      try {
        // Decode JWT to get user info (payload is base64url: -/_ must be
        // mapped to +// before atob, same as base64ToArrayBuffer in usePasskey)
        const payloadBase64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payloadJson = atob(payloadBase64);
        const payload = JSON.parse(payloadJson);

        const oauthUser = {
          id: payload.sub,
          email: payload.email,
          username: payload.username,
          organisation: payload.organisation,
          menu_group_id: payload.menu_group_id
        };

        // Persist the session under the keys the web auth gate actually reads
        // (auth_token/user_data + auth-token cookie — see BackendAuth.getToken/
        // getUser). Storing only the mobile keys 'access_token'/'user' leaves
        // isAuthenticated() false and the protected layout bounces straight
        // back to /signin (same defect the passkey login fixed in 70414b739).
        try {
          BackendAuth._clearUserBoundState?.();
        } catch {
          /* non-fatal */
        }
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_data', JSON.stringify(oauthUser));
        // Mobile/widget consumers still read the legacy keys — keep them in sync.
        localStorage.setItem('access_token', token);
        localStorage.setItem('user', JSON.stringify(oauthUser));
        try {
          BackendAuth.syncTokenToCookie?.(token);
        } catch {
          /* non-fatal */
        }

        // Store email for next login (Passkey prefill)
        if (payload.email) {
          localStorage.setItem('xoffi_last_login_email', payload.email);
        }

        // Clean URL
        window.history.replaceState({}, '', '/signin');

        // Show success message if new user
        if (isNew === 'true') {
          // console.log('[OAuth] New user created via OAuth');
        }

        // Redirect to return URL or dashboard (returnParam may be null —
        // isValidRedirectUrl(null) is true, so guard against navigating to '/null')
        const returnParam = params.get('return');
        const redirectTarget = isValidRedirectUrl(returnParam) && returnParam ? returnParam : '/dashboard';
        setTimeout(() => {
          window.location.href = redirectTarget;
        }, 100);
      } catch (error) {
        console.error('[OAuth] Failed to process token:', error);
        setError('Authentication failed - invalid token');
        setOauthCompleting(false); // Loader weg, Formular wieder zeigen
      }
    }
  }, []); // Run once on mount

  // Login background fallback (only for Theme API without optimized URLs)
  const setLoginBackgroundFallback = (backgroundPath) => {
    let finalBackgroundUrl;

    // Generic external asset handling (fallback for old Theme API)
    if (backgroundPath.startsWith('/vendor/') ||
        backgroundPath.startsWith('/backend/') ||
        backgroundPath.startsWith('/de/')) {
      const cleanPath = backgroundPath.startsWith('/') ? backgroundPath.substring(1) : backgroundPath;
      finalBackgroundUrl = `/api/external-assets/${cleanPath}`;
    } else {
      finalBackgroundUrl = backgroundPath;
    }

    // Preload image for smoother transition (fallback) with fade-in
    const img = new Image();
    img.onload = () => {
      // Inject <style> tag so URL resolves against document (not CDN CSS file)
      const absoluteUrl = finalBackgroundUrl.startsWith('/') ? `${window.location.origin}${finalBackgroundUrl}` : finalBackgroundUrl;
      let styleEl = document.getElementById('xoffi-auth-bg-style');
      if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = 'xoffi-auth-bg-style'; document.head.appendChild(styleEl); }
      styleEl.textContent = `body.auth-bg-custom::before { background-image: url('${absoluteUrl}') !important; }`;
      document.documentElement.style.setProperty('--auth-bg-image', `url('${absoluteUrl}')`);

      // Smooth fade-in of background image (fallback)
      setTimeout(() => {
        document.body.classList.add('bg-loaded');
      }, 150);
    };

    img.onerror = () => {
      // Fallback background image failed to load silently
    };

    img.src = finalBackgroundUrl;
  };

  // Set background image from cache immediately
  const setBackgroundImageFromCache = (backgroundUrl) => {
    // Inject <style> tag so URL resolves against document (not CDN CSS file)
    const absoluteUrl = backgroundUrl.startsWith('/') ? `${window.location.origin}${backgroundUrl}` : backgroundUrl;
    let styleEl = document.getElementById('xoffi-auth-bg-style');
    if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = 'xoffi-auth-bg-style'; document.head.appendChild(styleEl); }
    styleEl.textContent = `body.auth-bg-custom::before { background-image: url('${absoluteUrl}') !important; }`;
    document.documentElement.style.setProperty('--auth-bg-image', `url('${absoluteUrl}')`);

    // Show immediately without fade-in (from cache)
    document.body.classList.add('bg-loaded');
  };

  // Intelligent background optimization
  const optimizeBackgroundSize = (imageUrl) => {
    const img = new Image();
    img.onload = () => {
      const screenRatio = window.innerWidth / window.innerHeight;
      const imageRatio = img.naturalWidth / img.naturalHeight;

      // Intelligently choose between contain and cover
      if (Math.abs(screenRatio - imageRatio) < 0.3) {
        // Similar aspect ratios: cover shows more of the image
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center center';
      } else if (imageRatio > screenRatio * 1.2) {
        // Image is much wider: contain to use height
        document.body.style.backgroundSize = 'contain';
        document.body.style.backgroundPosition = 'center center';
      } else if (imageRatio < screenRatio * 0.8) {
        // Image is much taller: cover with top position
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center top';
      } else {
        // Standard: contain to ensure everything is visible
        document.body.style.backgroundSize = 'contain';
        document.body.style.backgroundPosition = 'center center';
      }
    };

    img.onerror = () => {
      // Image could not be loaded for optimization
      // Fallback: contain
      document.body.style.backgroundSize = 'contain';
    };

    img.src = imageUrl;
  };

  // Check LocalStorage for last email on mount
  useEffect(() => {
    const checkStoredEmail = async () => {
      try {
        const lastEmail = localStorage.getItem('xoffi_last_login_email');
        if (lastEmail && lastEmail.includes('@')) {
          // console.log('[Login] Found stored email:', lastEmail);

          // Fill form
          form.setValue('email', lastEmail);

          // Check Passkey availability
          setCheckingPasskey(true);
          try {
            const result = await checkAvailability(lastEmail);
            setPasskeyAvailable(result.available);
            setPasskeyDevices(result.devices || []);
            // console.log('[Login] Passkey check result:', result);
          } catch (error) {
            console.error('[Login] Passkey check failed:', error);
          } finally {
            setCheckingPasskey(false);
          }
        }
      } catch (error) {
        console.error('[Login] Error checking stored email:', error);
      }
    };

    checkStoredEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Automatic Passkey Availability Check (Amazon/Google-Style) - Manual Typing
  useEffect(() => {
    if (!debouncedEmail || !debouncedEmail.includes('@')) {
      setPasskeyAvailable(false);
      setPasskeyDevices([]);
      return;
    }

    const checkPasskeyAvailability = async () => {
      setCheckingPasskey(true);
      try {
        const result = await checkAvailability(debouncedEmail);
        setPasskeyAvailable(result.available);
        setPasskeyDevices(result.devices || []);
        // console.log('[Login] Passkey check (manual typing):', result);

        // NOTE: No automatic loginWithPasskey() trigger here. The availability
        // signal is account-scoped (does this user have ANY passkey?), NOT
        // device-scoped — a passkey registered on the laptop would otherwise
        // force the WebAuthn OS dialog on the phone (which can't satisfy it).
        // The user starts passkey login explicitly via the button; passive,
        // device-local surfacing is handled by Conditional UI (mediation).
      } catch (error) {
        console.error('[Login] Passkey check failed:', error);
        setPasskeyAvailable(false);
        setPasskeyDevices([]);
      } finally {
        setCheckingPasskey(false);
      }
    };

    checkPasskeyAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail]);

  // WebAuthn Conditional UI (autofill mediation) — passive, device-local passkey surfacing.
  // On mount, if the org enabled passkeys AND the browser supports conditional mediation,
  // start a background credentials.get(). The browser only offers a passkey if a discoverable
  // credential is physically present on THIS device, surfaced via the email-field autofill.
  // No modal, no forced OS dialog. Resolves only once the user actively picks a passkey.
  useEffect(() => {
    if (settingsLoading || !isPasskeyEnabled || oauthCompleting) return;

    const controller = new AbortController();
    let cancelled = false;

    const startConditionalUI = async () => {
      try {
        const supported = await isConditionalMediationAvailable();
        if (!supported || cancelled || controller.signal.aborted) return;

        conditionalAbortRef.current = controller;
        const result = await loginWithConditionalUI({ signal: controller.signal });

        if (result?.success) {
          // Tokens already stored in localStorage by the hook → hard redirect.
          redirectAfterLogin();
        }
        // result.aborted / unsupported / failure → stay silent, password & explicit
        // button remain available as fallback.
      } catch {
        // Defensive: hook already swallows AbortError/NotAllowedError.
      }
    };

    startConditionalUI();

    return () => {
      cancelled = true;
      controller.abort();
      if (conditionalAbortRef.current === controller) {
        conditionalAbortRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading, isPasskeyEnabled, oauthCompleting]);

  // Passkey Login Handler (explicit button → email-based, endpoint 1).
  const handlePasskeyLogin = async () => {
    // Abort the passive conditional get so two WebAuthn credentials.get() never compete.
    abortConditionalUI();
    setIsProcessing(true);
    setError(null);

    try {
      // console.log('[Login] Starting passkey login for:', emailValue);
      await loginWithPasskey(emailValue);
      // console.log('[Login] Passkey login successful, redirecting...');

      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 100);
    } catch (err) {
      console.error('[Login] Passkey login error:', err);
      setError(err.message || 'Passkey login failed. Please try password login.');
      // Show alternative login options on Passkey failure
      setShowAlternativeLogin(true);
    } finally {
      setIsProcessing(false);
    }
  };

  async function onSubmit(values) {
    // Password login commits the user to a non-passkey flow — abort the passive
    // conditional credentials.get so two WebAuthn calls never compete.
    abortConditionalUI();
    setIsProcessing(true);
    setError(null);

    try {
      // console.log('[Signin] Form validation passed');
      // console.log('[Signin] Raw form values:', JSON.stringify(values, null, 2));
      // console.log('[Signin] Email field exists:', 'email' in values);
      // console.log('[Signin] Email field value type:', typeof values.email);
      // console.log('[Signin] Email field value:', `"${values.email}"`);
      // console.log('[Signin] Password field value:', values.password ? '[PROVIDED]' : '[EMPTY]');

      // Ensure email field is not undefined or null
      const loginValue = values.email || '';
      // console.log('[Signin] Cleaned email value:', `"${loginValue}"`);

      if (!loginValue.trim()) {
        console.error('[Signin] Email field is empty after cleaning');
        setError('Please enter your username or email');
        return;
      }

      // Use BackendAuth for direct login to /api/auth/login
      const loginData = {
        email: loginValue,  // Send as email field - can be username or email
        password: values.password || '',
      };
      // console.log('[Signin] Final login data being sent:', JSON.stringify(loginData, null, 2));

      const result = await BackendAuth.login(loginData);

      // Mehrere Konten mit dieser Adresse, bei denen das Passwort passt:
      // Auswahl anzeigen statt anzumelden (jedes Konto ist ein eigener Zugang).
      if (result?.select_organisation && result?.selection_token) {
        setAccountSelection({
          token: result.selection_token,
          organisationen: Array.isArray(result.organisationen) ? result.organisationen : [],
          loginValue,
        });
        return;
      }

      processLoginResult(result, loginValue);
    } catch (err) {
      console.error('[Signin] Login error:', err);
      handleLoginError(err, values?.email);
    } finally {
      setIsProcessing(false);
    }
  }

  // Gemeinsame Verarbeitung einer erfolgreichen /login- bzw. /login/select-Antwort
  function processLoginResult(result, loginValue) {
    if (result?.success) {
      // Store email in localStorage for next visit (Passkey prefill)
      if (loginValue && loginValue.includes('@')) {
        localStorage.setItem('xoffi_last_login_email', loginValue);
        // console.log('[Signin] Stored email in localStorage:', loginValue);
      }

      // Check if MFA is required
      if (result.mfa_required && result.temp_token) {
        // console.log('[Signin] MFA required - showing verification modal');
        setTempToken(result.temp_token);
        setShowMFAModal(true);
        setIsProcessing(false);
        return;
      }

      // console.log('[Signin] Login successful, processing redirect');
      // console.log('[Signin] User data:', result.data?.user);

      // Check if user should setup passkey (Amazon/Google-style prompt)
      if (!passkeyAvailable && isPasskeyEnabled) {
        const currentUser = BackendAuth.getUser();
        const dismissedKey = currentUser?.id ? `xoffi_passkey_dismissed_${currentUser.id}` : null;
        const wasDismissed = dismissedKey && localStorage.getItem(dismissedKey);

        if (!wasDismissed) {
          // console.log('[Signin] User has no passkey - showing setup modal');
          setIsProcessing(false);
          setShowPasskeySetupModal(true);
          return;
        }
      }

      // Get return URL from state (extracted from window.location on mount)
      const redirectTarget = returnUrl || '/dashboard';

      // console.log('[Signin] Return URL from state:', returnUrl);
      // console.log('[Signin] Final redirect target:', redirectTarget);

      // Kleine Verzögerung um sicherzustellen, dass localStorage gesetzt ist
      setTimeout(() => {
        // console.log('[Signin] Executing redirect to', redirectTarget);
        // console.log('[Signin] Checking auth status:', BackendAuth.isAuthenticated());
        // console.log('[Signin] Token:', BackendAuth.getToken() ? 'exists' : 'missing');
        // console.log('[Signin] User:', BackendAuth.getUser());

        // Verwende window.location.href für eine harte Weiterleitung
        // Dies stellt sicher, dass die Seite komplett neu lädt
        window.location.href = redirectTarget;
      }, 100);
    } else {
      // console.log('[Signin] Login failed:', result?.message);
      setError(result?.message || 'Invalid credentials. Please try again.');
    }
  }

  function handleLoginError(err, fallbackEmail) {
    // 403 requires_verification: the email signup hasn't confirmed its address
    // yet. Backend contract: 403 { detail: { requires_verification: true,
    // email, message } }. Route the user to the existing email-code verify
    // view (which also offers "resend code") instead of showing a generic
    // login failure. Email is carried over via query param.
    const detail = err?.payload?.detail;
    if (err?.status === 403 && detail?.requires_verification === true) {
      const verifyEmail = detail.email || (fallbackEmail || '').trim();
      const query = verifyEmail ? `?email=${encodeURIComponent(verifyEmail)}` : '';
      router.push(`/verify-email${query}`);
      return;
    }

    setError(
      err instanceof Error
        ? err.message
        : 'Connection error. Please check your internet connection and try again.',
    );
  }

  async function onSelectAccount(key) {
    if (!accountSelection || selectingKey) return;
    setSelectingKey(key);
    setError(null);
    try {
      const result = await BackendAuth.loginSelect(accountSelection.token, key);
      const loginValue = accountSelection.loginValue;
      setAccountSelection(null);
      processLoginResult(result, loginValue);
    } catch (err) {
      console.error('[Signin] Account selection error:', err);
      // Auswahl ist einmalig/5 Minuten gueltig → zurueck zum Formular
      setAccountSelection(null);
      if (err?.status === 401) {
        setError(t('auth.signin.select_account_expired', 'Die Auswahl ist abgelaufen. Bitte melde dich erneut an.'));
      } else {
        handleLoginError(err, accountSelection.loginValue);
      }
    } finally {
      setSelectingKey(null);
    }
  }

  // eID/OAuth-Callback wird verarbeitet: Loader statt Formular — sonst blitzt
  // die Signin-Maske (inkl. E-Mail-Prefill/Passkey-UI) kurz auf, bevor der
  // oauth_success-Handler die Session speichert und weiterleitet.
  if (oauthCompleting) {
    return <ScreenLoader />;
  }

  return (
    // Kein Card-weiter Blur waehrend des Domain-Loads: das Formular ist sofort
    // bedienbar und soll auch so aussehen — der Branding-Header hat eigene Skeletons.
    <div className="auth-card w-full max-w-md mx-auto">
      {/* Domain-specific header with MySQL data */}
      <div className="text-center mb-11">
        {isLoadingDomain ? (
          /* Loading skeleton */
          <div className="login-title text-center animate-pulse">
            {/* Title Skeleton */}
            <div className="h-8 bg-gray-300 rounded-lg mb-3 w-48 mx-auto"></div>
            {/* Description Skeleton */}
            <div className="h-4 bg-gray-200 rounded-lg w-64 mx-auto"></div>
          </div>
        ) : domainInfo?.domain ? (
          <div className="login-title text-center animate-fade-in">
            {/* Logo only if optimized URL available */}
            {(domainInfo.domain.logo_light_url || domainInfo.domain.logo_dark_url) && (
              <div className="logo-container mb-4">
                {/* Use only optimized URLs from backend */}
                {domainInfo.domain.logo_light_url && (
                  <img
                    src={domainInfo.domain.logo_light_url}
                    alt={`${domainInfo.domain.name_login || 'Company'} Logo`}
                    className="mx-auto max-h-20 w-auto dark:hidden transition-opacity duration-300"
                  />
                )}
                {domainInfo.domain.logo_dark_url && (
                  <img
                    src={domainInfo.domain.logo_dark_url}
                    alt={`${domainInfo.domain.name_login || 'Company'} Logo`}
                    className="mx-auto max-h-20 w-auto hidden dark:block transition-opacity duration-300"
                  />
                )}
              </div>
            )}

            {/* MySQL title (name_login has priority) */}
            <h2 className="text-dark fw-bolder mb-3 text-xl font-semibold transition-all duration-300">
              {domainInfo.domain.name_login || domainInfo.domain.title || 'Anmelden'}
            </h2>


          </div>
        ) : (
          /* No fallback - only show if domain data available */
          <div></div>
        )}
        <div className="text-gray-500 fw-semibold fs-6 transition-all duration-300">
          {isLoadingDomain ? (
            <div className="h-4 bg-gray-200 rounded-lg w-56 mx-auto animate-pulse"></div>
          ) : (
            domainInfo?.domain?.description || ''
          )}
        </div>
      </div>

      {/* Passkey Login Button (explicit fallback for browsers without Conditional UI).
          Gated on isPasskeyEnabled so it only appears when the ORG enabled passkeys —
          passkeyAvailable alone (account has a passkey somewhere) is too broad. */}
      {!settingsLoading && isPasskeyEnabled && passkeyAvailable && emailValue && (
        <div className="mb-6">
          <button
            type="button"
            onClick={handlePasskeyLogin}
            disabled={isProcessing || passkeyLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="font-medium">
              {/* Generic label only — never name a specific (possibly remote)
                  device. WebAuthn's own picker handles same-device vs. the
                  cross-device hybrid flow. */}
              {t('auth.signin.passkey_login', 'Mit Passkey anmelden')}
            </span>
          </button>

          {/* Alternative Login Options Link */}
          {!showAlternativeLogin && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowAlternativeLogin(true)}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.signin.other_options', 'Andere Anmeldemöglichkeiten')}
              </button>
            </div>
          )}

          {/* Separator - nur wenn alternative Optionen angezeigt werden */}
          {showAlternativeLogin && (
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('auth.signin.or_divider', 'ODER')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Passkey wird gerade geprüft... */}
      {checkingPasskey && emailValue && (
        <div className="mb-4 text-center text-sm text-gray-500">
          {t('auth.signin.checking_passkey', 'Prüfe Passkey-Verfügbarkeit...')}
        </div>
      )}

      {/* OAuth Social Login Buttons - nur anzeigen wenn kein Passkey ODER Alternative Login gewünscht */}
      {!settingsLoading && ((isOAuthEnabled && enabledOAuthProviders.length > 0) || isIdAustriaAvailable) && (!passkeyAvailable || showAlternativeLogin) && (
        <div className="mb-6">
          {isOAuthEnabled && enabledOAuthProviders.length > 0 && (
            <OAuthButtons
              enabledProviders={enabledOAuthProviders}
              className="w-full"
            />
          )}

          {/* ID Austria (E-ID) — own start route /id_austria/login/start, own gate */}
          {isIdAustriaAvailable && (
            <div className={isOAuthEnabled && enabledOAuthProviders.length > 0 ? 'mt-2' : ''}>
              <IdAustriaButton mode="login" returnTo={returnUrl || undefined} />
            </div>
          )}

          {/* Separator */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-background text-muted-foreground">{t('auth.signin.or_divider', 'ODER')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Konto-Auswahl: dieselbe E-Mail-Adresse + Passwort passen zu mehreren
          Konten (je ein eigener Zugang pro Organisation). Liste kommt vom
          Backend und enthaelt NUR Konten, deren Passwort gestimmt hat. */}
      {accountSelection ? (
        <div className="space-y-4" role="region" aria-labelledby="xoffi-account-select-title">
          <div className="text-center">
            <h3 id="xoffi-account-select-title" className="text-lg font-semibold text-foreground">
              {t('auth.signin.select_account_title', 'Konto auswählen')}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(
                'auth.signin.select_account_hint',
                'Zu dieser E-Mail-Adresse gibt es mehrere Konten. Wähle aus, mit welchem du dich anmelden möchtest.',
              )}
            </p>
          </div>

          <ul className="space-y-2">
            {accountSelection.organisationen.map((o) => (
              <li key={o.key}>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-auto justify-between py-3 text-start"
                  disabled={!!selectingKey}
                  onClick={() => onSelectAccount(o.key)}
                >
                  <span className="flex min-w-0 flex-col items-start">
                    <span className="truncate font-semibold text-foreground">
                      {o.organisation_name || t('auth.signin.select_account_no_org', 'Ohne Organisation')}
                    </span>
                    {o.username && (
                      <span className="truncate text-xs text-muted-foreground">
                        {t('auth.signin.select_account_username', 'Benutzername')}: {o.username}
                      </span>
                    )}
                  </span>
                  {selectingKey === o.key && (
                    <LoaderCircleIcon className="size-4 shrink-0 animate-spin text-muted-foreground" />
                  )}
                </Button>
              </li>
            ))}
          </ul>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={!!selectingKey}
            onClick={() => {
              setAccountSelection(null);
              setError(null);
            }}
          >
            {t('auth.signin.select_account_back', 'Zurück zur Anmeldung')}
          </Button>
        </div>
      ) : (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="form w-100 space-y-5"
          noValidate
          id="kt_sign_in_form"
          action="/api/auth/login"
          method="post"
        >
          {error && (
            <Alert variant="destructive">
              <AlertIcon>
                <AlertCircle />
              </AlertIcon>
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          )}

          {/* Email Input - nur anzeigen wenn Password-Login ODER Passkey aktiviert */}
          {(isPasswordEnabled || isPasskeyEnabled) && (
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={t('auth.signin.email_placeholder', { defaultValue: 'Benutzername oder E-Mail', editable: false })}
                      {...field}
                      className="form-control bg-active-secondary"
                      // "webauthn" token is REQUIRED for Conditional UI — without it the
                      // browser surfaces no passkey autofill into this field.
                      autoComplete="username webauthn"
                      name="username"
                      required
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Password Field - nur anzeigen wenn Password-Login aktiviert UND (kein Passkey ODER Alternative Login gewünscht) */}
          {isPasswordEnabled && (!passkeyAvailable || showAlternativeLogin) && (
            <>
              {/* Laravel-Style Password Input */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <Input
                        placeholder={t('auth.signin.password_placeholder', { defaultValue: 'Passwort', editable: false })}
                        type={passwordVisible ? 'text' : 'password'}
                        {...field}
                        className="form-control bg-active-secondary"
                        autoComplete="current-password"
                        name="password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        mode="icon"
                        size="sm"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        className="absolute end-0 top-1/2 -translate-y-1/2 h-7 w-7 me-1.5 bg-transparent!"
                        aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                      >
                        {passwordVisible ? (
                          <EyeOff className="text-muted-foreground" />
                        ) : (
                          <Eye className="text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Laravel-Style Footer */}
              <div className="flex justify-end mb-8">
                <Link
                  href="/reset-password"
                  className="link-primary text-sm font-semibold text-foreground hover:text-primary"
                >
                  {t('auth.signin.forgot_password', 'Passwort vergessen?')}
                </Link>
              </div>

              {/* Laravel-Style Submit Button */}
              <div className="mb-10">
                <Button
                  type="submit"
                  disabled={isProcessing}
                  className="w-full"
                  id="kt_sign_in_submit"
                >
                  {isProcessing ? (
                    <LoaderCircleIcon className="size-4 animate-spin mr-2" />
                  ) : null}
                  {t('auth.signin.continue', 'Anmelden')}
                </Button>
              </div>
            </>
          )}
        </form>
      </Form>
      )}

      {/* Sign Up Link - only show if registration is enabled for this domain */}
      {signupEnabled && (
        <div className="text-sm text-center mt-6">
          <span className="auth-register-glass text-foreground font-medium">
            {t('auth.signin.no_account', 'Noch kein Konto?')}{' '}
            <Link
              href="/signup"
              className="font-semibold text-primary hover:text-primary/80"
            >
              {t('auth.signin.signup_link', 'Registrieren')}
            </Link>
          </span>
        </div>
      )}

      {/* ID Austria JIT-Link Dialog (?link_idaustria=<token>) */}
      {idAustriaLinkToken && (
        <IdAustriaLinkDialog
          linkToken={idAustriaLinkToken}
          returnTo={returnUrl || undefined}
          onClose={() => setIdAustriaLinkToken(null)}
        />
      )}

      {/* MFA Verification Modal */}
      {showMFAModal && tempToken && (
        <MFAVerificationModal
          tempToken={tempToken}
          onSuccess={() => {
            setShowMFAModal(false);
            window.location.href = returnUrl || '/dashboard';
          }}
          onCancel={() => {
            setShowMFAModal(false);
            setTempToken(null);
            setIsProcessing(false);
          }}
        />
      )}

      {/* Passkey Setup Modal (Amazon/Google-Style) */}
      {showPasskeySetupModal && (
        <PasskeySetupModal
          show={showPasskeySetupModal}
          onClose={() => {
            setShowPasskeySetupModal(false);
            window.location.href = returnUrl || '/dashboard';
          }}
          onSuccess={() => {
            setShowPasskeySetupModal(false);
            window.location.href = returnUrl || '/dashboard';
          }}
          onDismiss={() => {
            const currentUser = BackendAuth.getUser();
            if (currentUser?.id) {
              localStorage.setItem(`xoffi_passkey_dismissed_${currentUser.id}`, 'true');
            }
            setShowPasskeySetupModal(false);
            window.location.href = returnUrl || '/dashboard';
          }}
        />
      )}
    </div>
  );
}
